import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Badge, Button, ConfirmDialog, IconButton, Input,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner, Textarea,
} from '@rentflowdev/ui'
import {
	dealMessagesApi,
	dealsApi,
	useCreateDealMeetingMutation,
	useDeleteDealMeetingMutation,
	useListDealMeetingsQuery,
	useUpdateDealMeetingMutation,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { clearOverride } from '@/store/deal-stage-overrides.slice'
import type { DealMeetingStatus } from '@/types'
import {
	DEAL_MEETING_STATUSES, DEAL_MEETING_STATUS_KEYS, DEAL_MEETING_STATUS_VARIANTS,
	DEFAULT_LOCALE,
} from '@/types'

/**
 * Строгое подмножество Prisma `DealMeeting`: время, адрес, заметка.
 *
 * `status` сюда не входит намеренно — его меняют списком в строке, а не в
 * форме, и держать его здесь значило бы иметь два места для одного ответа.
 */
const meetingSchema = z
	.object({
		scheduledAt: z.string().min(1, 'deals.meetings.validation.required'),
		address:     z.string().trim().min(1, 'deals.meetings.validation.required'),
		notes:       z.string().optional(),
	})
	.superRefine((values, ctx) => {
		// Встречу назначают, поэтому прошедшая дата — всегда описка, чаще всего
		// в годе. Проверяем по реальным часам: форма могла провисеть открытой.
		if (values.scheduledAt && new Date(values.scheduledAt).getTime() < Date.now()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['scheduledAt'],
				message: 'deals.meetings.validation.pastDate',
			})
		}
	})

type MeetingFormValues = z.infer<typeof meetingSchema>

const EMPTY_MEETING: MeetingFormValues = { scheduledAt: '', address: '', notes: '' }

interface DealMeetingSectionProps {
	dealId: string
	propertyAddress?: string
	adding?: boolean
	onAddingChange?: (v: boolean) => void
}

function formatDateTime(iso: string): string {
	const date = new Date(iso)
	const day  = date.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	return `${day}, ${time}`
}

/**
 * `datetime-local` speaks local wall-clock time, while `toISOString` is UTC —
 * so the bound has to be shifted by the offset or it lands an hour or more off
 * for anyone east of Greenwich.
 */
function toLocalDateTimeValue(date: Date): string {
	const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
	return shifted.toISOString().slice(0, 16)
}

export function DealMeetingSection({ dealId, propertyAddress, adding: addingProp, onAddingChange }: DealMeetingSectionProps) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { data, isLoading } = useListDealMeetingsQuery({ dealId })
	const [createMeeting, { isLoading: isCreating }] = useCreateDealMeetingMutation()
	const [updateMeeting] = useUpdateDealMeetingMutation()
	const [deleteMeeting] = useDeleteDealMeetingMutation()

	const [addingLocal, setAddingLocal] = useState(false)
	const adding    = addingProp    ?? addingLocal
	const setAdding = onAddingChange ?? setAddingLocal
	const form = useForm<MeetingFormValues>({
		resolver: zodResolverI18n<MeetingFormValues>(meetingSchema),
		defaultValues: EMPTY_MEETING,
		mode: 'onBlur',
	})
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
	/**
	 * Which meeting the form is editing, if any.
	 *
	 * The same form serves both: a meeting is edited with exactly the fields it
	 * was created with, and a second copy of them would be one more place for
	 * the two to disagree. Status stays a select on the row: it changes far more
	 * often than the rest, and opening a form to move one dropdown is a step
	 * nobody needs.
	 */
	const [editingId, setEditingId] = useState<string | null>(null)
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)

	const meetings = data?.items ?? []

	// A meeting is something you arrange, so a past date is always a slip —
	// most often a mistyped year. `min` alone would not do: it is advisory in
	// several browsers and sidestepped by typing rather than picking, so the
	// same condition also blocks submit.
	//
	// Anchored to when the form opened rather than read on every render: the
	// clock is impure, and a bound that crept forward mid-typing would reject a
	// time the user had only just picked. handleSubmit re-checks against the
	// real clock, so a form left open for hours still cannot post a past date.
	// `adding` looks redundant to the linter — nothing in the body reads it —
	// but it is the point: reopening the form re-anchors the clock.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const openedAt = useMemo(() => new Date(), [adding])
	const minScheduledAt = useMemo(() => toLocalDateTimeValue(openedAt), [openedAt])


	const reset = () => {
		setAdding(false)
		setEditingId(null)
		form.reset(EMPTY_MEETING)
	}

	function startEditing(meeting: { id: string; scheduledAt: string; address: string; notes?: string }) {
		setEditingId(meeting.id)
		setAdding(true)
		form.reset({
			scheduledAt: toLocalDateTimeValue(new Date(meeting.scheduledAt)),
			address:     meeting.address,
			notes:       meeting.notes ?? '',
		})
	}

	async function handleUpdate(id: string, scheduledAtIso: string, values: MeetingFormValues) {
		try {
			await updateMeeting({
				id,
				scheduledAt: scheduledAtIso,
				address:     values.address.trim(),
				// Emptied on purpose is an answer too, so '' is sent rather than
				// dropped — otherwise a note could be added and never removed.
				notes:       values.notes?.trim() ?? '',
			}).unwrap()
			reset()
		} catch (err) {
			setActionError({ title: t('deals.meetings.errors.update'), description: extractErrorMessage(err) })
		}
	}

	async function onSubmit(values: MeetingFormValues) {
		setActionError(null)
		const scheduledAtIso = new Date(values.scheduledAt).toISOString()
		if (editingId) {
			await handleUpdate(editingId, scheduledAtIso, values)
			return
		}
		try {
			await createMeeting({
				dealId,
				scheduledAt: scheduledAtIso,
				address:     values.address.trim(),
				notes:       values.notes?.trim() || undefined,
			}).unwrap()
			reset()
			// Scheduling a meeting can auto-advance the deal's stage on the backend
			// (NEW/CONTACT/NEGOTIATION -> VIEWING). dealMeetingsApi only invalidates its
			// own DealMeeting tag, so refresh dealsApi's cache too or the stage stepper
			// and sidebar stay stale until an unrelated refetch happens.
			dispatch(dealsApi.util.invalidateTags([{ type: 'Deal', id: dealId }]))
			// A lingering local stage override (from a kanban drag or stepper click)
			// would mask the refetched auto-advanced stage — drop it.
			dispatch(clearOverride(dealId))
			// The client-facing chat notice is posted by crm-service after the meeting
			// commits (see DealMeetingsService.sendMeetingChatNotice). The browser
			// never learns of it, and dealMessagesApi is a separate createApi, so no
			// tag from this slice can reach it — without an explicit invalidation the
			// notice, and a FAILED send in particular, stays invisible until the page
			// is reloaded.
			dispatch(dealMessagesApi.util.invalidateTags([{ type: 'DealMessage', id: dealId }]))
		} catch (err) {
			setActionError({ title: t('deals.meetings.errors.create'), description: extractErrorMessage(err) })
		}
	}

	async function handleDelete(meetingId: string) {
		setActionError(null)
		try {
			await deleteMeeting(meetingId).unwrap()
		} catch (err) {
			setActionError({ title: t('deals.meetings.errors.delete'), description: extractErrorMessage(err) })
		}
	}

	function confirmDelete() {
		if (deleteTargetId) void handleDelete(deleteTargetId)
		setDeleteTargetId(null)
	}

	if (isLoading) {
		return <Spinner size="sm" />
	}

	return (
		<div className="flex flex-col gap-3">
			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}
			{meetings.length === 0 && !adding && (
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.meetings.empty')}</p>
			)}

			{meetings.filter((m) => m.id !== editingId).map((m) => (
				<div
					key={m.id}
					className="flex items-start gap-3 rounded-md border border-(--color-border) bg-(--color-background) p-3"
				>
					<div className="flex flex-1 min-w-0 flex-col gap-1">
						<div className="flex items-center gap-2">
							<span className="text-sm font-[500] text-(--color-foreground)">{formatDateTime(m.scheduledAt)}</span>
							<Badge variant={DEAL_MEETING_STATUS_VARIANTS[m.status]} size="sm">
								{t(DEAL_MEETING_STATUS_KEYS[m.status])}
							</Badge>
						</div>
						<span className="text-sm text-(--color-muted-foreground)">{m.address}</span>
						{m.notes && (
							<p className="text-sm text-(--color-foreground) whitespace-pre-wrap">{m.notes}</p>
						)}
					</div>
					<Select
						value={m.status}
						onValueChange={(v) => void updateMeeting({ id: m.id, status: v as DealMeetingStatus })}
					>
						<SelectTrigger className="w-40 h-8">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{DEAL_MEETING_STATUSES.map((s) => (
								<SelectItem key={s} value={s}>{t(DEAL_MEETING_STATUS_KEYS[s])}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => startEditing(m)}
						aria-label={t('common.edit')}
					>
						<PencilIcon className="size-4" />
					</IconButton>
					<IconButton
						variant="ghost"
						size="sm"
						type="button"
						onClick={() => setDeleteTargetId(m.id)}
						aria-label={t('common.delete')}
					>
						<Trash2Icon className="size-4" />
					</IconButton>
				</div>
			))}

			<ConfirmDialog
				open={!!deleteTargetId}
				onOpenChange={(open) => { if (!open) setDeleteTargetId(null) }}
				title={t('deals.meetings.deleteDialog.title')}
				description={t('deals.meetings.deleteDialog.description')}
				confirmLabel={t('common.delete')}
				cancelLabel={t('common.cancel')}
				variant="destructive"
				onConfirm={confirmDelete}
			/>

			{adding ? (
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col gap-2 rounded-md border border-(--color-border) bg-(--color-background) p-3"
					>
						<div className="grid grid-cols-2 gap-2">
							<FormField
								control={form.control}
								name="scheduledAt"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-xs text-(--color-muted-foreground)">
											{t('deals.meetings.scheduledAt')}
										</FormLabel>
										<FormControl>
											<Input type="datetime-local" min={minScheduledAt} {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="address"
								render={({ field }) => (
									<FormItem>
										<div className="flex items-center justify-between">
											<FormLabel className="text-xs text-(--color-muted-foreground)">
												{t('deals.meetings.address')}
											</FormLabel>
											{propertyAddress && (
												<button
													type="button"
													className="text-xs text-(--color-primary) hover:underline"
													onClick={() => form.setValue('address', propertyAddress, { shouldValidate: true })}
												>
													{t('deals.meetings.fillFromProperty')}
												</button>
											)}
										</div>
										<FormControl>
											<Input placeholder={t('deals.meetings.addressPlaceholder')} {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<FormField
							control={form.control}
							name="notes"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-xs text-(--color-muted-foreground)">
										{t('deals.meetings.notes')}
									</FormLabel>
									<FormControl>
										<Textarea
											placeholder={t('deals.meetings.notesPlaceholder')}
											className="min-h-16"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<div className="flex gap-2 justify-end">
							<Button type="button" variant="secondary" size="sm" onClick={reset}>
								{t('deals.meetings.cancel')}
							</Button>
							<Button type="submit" size="sm" disabled={isCreating}>
								{editingId ? t('common.save') : t('deals.meetings.submit')}
							</Button>
						</div>
					</form>
				</Form>
			) : (
				<Button
					variant="outline"
					size="sm"
					iconLeft={<PlusIcon className="size-4" />}
					className="self-start"
					onClick={() => setAdding(true)}
				>
					{t('deals.meetings.add')}
				</Button>
			)}
		</div>
	)
}
